import { useEffect, useMemo, useState } from "react";
import { useTripStore } from "../store/tripStore";
import { useUIStore } from "../store/uiStore";
import { useLibraryStore } from "../store/libraryStore";
import { useLegStore } from "../store/legStore";
import { legColorPickerOptions } from "../theme/colors";
import { Button } from "./Button";
import { Modal } from "./Modal";
import { ColorPicker, FormInput, FormLabel } from "./FormFields";

/**
 * Opened by clicking a leg bar in the calendar's all-day band. Places join a
 * leg by being dragged onto its bar, so this editor only removes them.
 */
export function LegEditorModal() {
  const activeEditor = useUIStore((state) => state.activeEditor);
  const closeEditor = useUIStore((state) => state.closeEditor);
  const setSidebarLegFocus = useUIStore((state) => state.setSidebarLegFocus);
  const trip = useTripStore((state) => state.trip);
  const isReadOnly = useTripStore((state) => state.isReadOnly());
  const pois = useLibraryStore((state) => state.pois);
  const legs = useLegStore((state) => state.legs);
  const updateLeg = useLegStore((state) => state.updateLeg);
  const deleteLeg = useLegStore((state) => state.deleteLeg);
  const removePoiFromLeg = useLegStore((state) => state.removePoiFromLeg);

  const open = activeEditor?.type === "leg";
  const leg = useMemo(
    () =>
      open && activeEditor?.entityId
        ? (legs.find((entry) => entry.id === activeEditor.entityId) ?? null)
        : null,
    [open, activeEditor, legs],
  );

  // The name commits on blur or Enter rather than per keystroke: updateLeg is
  // a PATCH that bumps the trip revision, so typing would fire one request per
  // character and let out-of-order responses rewrite what was typed.
  const [nameDraft, setNameDraft] = useState("");
  useEffect(() => {
    setNameDraft(leg?.name ?? "");
  }, [leg?.id, leg?.name]);

  const members = useMemo(() => {
    if (!leg) return [];
    const byId = new Map(pois.map((poi) => [poi.id, poi]));
    return leg.poiIds
      .map((poiId) => byId.get(poiId))
      .filter((poi): poi is (typeof pois)[number] => poi !== undefined);
  }, [leg, pois]);

  if (!open || !leg || !trip) return null;

  const patch = (updates: Parameters<typeof updateLeg>[2]) => {
    void updateLeg(trip.id, leg.id, updates);
  };

  const commitName = () => {
    const name = nameDraft.trim();
    if (!name) {
      setNameDraft(leg.name);
      return;
    }
    if (name !== leg.name) patch({ name });
  };

  return (
    <Modal
      open
      onClose={closeEditor}
      title={leg.name}
      description={`${leg.startDate} – ${leg.endDate}. Legs may overlap, so a handoff day belongs to both.`}
      icon="edit_calendar"
      size="md"
      footer={
        <>
          {!isReadOnly && (
            <Button
              variant="danger"
              icon="delete"
              onClick={() => {
                void deleteLeg(trip.id, leg.id);
                closeEditor();
              }}
            >
              Delete
            </Button>
          )}
          <Button
            variant="secondary"
            icon="filter_center_focus"
            onClick={() => {
              setSidebarLegFocus(leg.id);
              closeEditor();
            }}
          >
            Focus sidebar
          </Button>
          <Button variant="primary" onClick={closeEditor}>
            Done
          </Button>
        </>
      }
    >
      <div className="space-y-3">
        <div>
          <FormLabel>Name</FormLabel>
          <FormInput
            autoFocus
            value={nameDraft}
            disabled={isReadOnly}
            onChange={setNameDraft}
            onBlur={commitName}
            onKeyDown={(event) => {
              if (event.key === "Enter") {
                event.preventDefault();
                commitName();
              }
            }}
          />
        </div>

        <div className="grid gap-3 sm:grid-cols-2">
          <div>
            <FormLabel>Start</FormLabel>
            <FormInput
              type="date"
              value={leg.startDate}
              disabled={isReadOnly}
              onChange={(value) =>
                // A cleared date input reports "", which sorts before every
                // real date and would otherwise be patched through as a date.
                value !== "" &&
                value <= leg.endDate &&
                patch({ startDate: value })
              }
            />
          </div>
          <div>
            <FormLabel>End</FormLabel>
            <FormInput
              type="date"
              value={leg.endDate}
              disabled={isReadOnly}
              onChange={(value) =>
                value !== "" &&
                value >= leg.startDate &&
                patch({ endDate: value })
              }
            />
          </div>
        </div>

        <div>
          <FormLabel>Colour</FormLabel>
          <div className="mt-1">
            <ColorPicker
              selectedIndex={leg.color}
              onChange={(index) => patch({ color: index })}
              options={legColorPickerOptions}
              ariaLabel="Leg colour"
            />
          </div>
        </div>

        <div>
          <FormLabel>Places · {members.length}</FormLabel>
          {members.length === 0 ? (
            <p className="mt-1 text-xs italic text-tertiary">
              Drag a place from the sidebar onto this leg to add it.
            </p>
          ) : (
            <ul className="mt-1 max-h-52 divide-y divide-border overflow-y-auto rounded-lg border border-border">
              {members.map((poi) => (
                <li
                  key={poi.id}
                  className="flex items-center justify-between gap-2 px-2 py-1"
                >
                  <span className="truncate text-xs text-secondary">
                    {poi.name}
                  </span>
                  {!isReadOnly && (
                    <Button
                      variant="icon"
                      size="sm"
                      icon="close"
                      aria-label={`Remove ${poi.name} from ${leg.name}`}
                      onClick={() =>
                        void removePoiFromLeg(trip.id, leg.id, poi.id)
                      }
                    />
                  )}
                </li>
              ))}
            </ul>
          )}
        </div>
      </div>
    </Modal>
  );
}
